import React, { useEffect, useState } from 'react';
import { useIntl, useSelector } from 'umi';
import CardCommon, { CardCommonType } from '@op/components/CardCommon';
import useDefaultSummary, {
  RequestFuncMapType,
} from '../hooks/useDefaultSummary';
import PromptModal from '../PromptModal';
import usePromptModal from '../hooks/usePromptModal';
import { copyIcon, fullIcon, refreshIcon } from '@op/constants/chart'; 
import useFullDialog from '../hooks/useFullDialog';
import { GPTListType, MultipleGptCardView } from '../components/GptCardView';
import useMultipleGpt from '../hooks/useMultipleGpt';
import '../styles/index.scss';
import { PromptListType } from '../types';
import { downloadAction } from '@op/pages/download/utils';
import useRequestFeedsState from '../hooks/useRequestFeedsState';
import { DBO_T } from '@op/utils/format';

type PageCardType = {
  filter: any;
  backGPTText?: (value: string) => void;
  stopAction?: () => void;
  errorCallback?: () => void;
  hasStopBtn?: boolean;
  requestFuncMap?: RequestFuncMapType;
  autoRequest?: boolean;
  children: (
    open: (value?: boolean) => void,
    options: { loading?: boolean; total: number },
  ) => any;
  GPTList: GPTListType;
  promptList?: PromptListType | ((wholeGameInfo: any) => PromptListType);
  oriFeedsState?: any; // 外部的state数据
  fileName?: string;
  saveName?:string;
} & Omit<CardCommonType, 'children'>;

/**
 * 当前页面展示GPT文案的卡片
 * @param param0
 * @returns
 */
export default function MultiplePageCard({
  filter,
  backGPTText,
  stopAction,
  errorCallback,
  hasStopBtn = false,
  requestFuncMap,
  autoRequest = false,
  children,
  GPTList,
  promptList,
  fileName = 'AI Summary',
  title = 'AI Summary',
  oriFeedsState,
  saveName,
  ...cardProps
}: PageCardType) {
  const { formatMessage } = useIntl();
  const { setPromptInfo, promptInfo, visible, toggleVisible } = usePromptModal(
    {},
  );

  const [showChildren, setShowChildren] = useState(true);

  const {
    loading: stateLoading,
    requestFeedsStateData,
    total,
  } = useRequestFeedsState({
    filter,
    needRequest: oriFeedsState === undefined,
    oriFeedsState,
  });

  const { loading, defaultGptText, clearDefaultGpt } = useDefaultSummary({
    filter,
    requestFuncMap,
    stateLoading,
    requestFeedsStateData,
  });

  const {
    status,
    changeAllGptMap,
    renderHtml,
    isEmpty,
    summary,
    showDefaultGpt,
    getTitle,
  } = useMultipleGpt({
    defaultGptText,
  });

  const { FullDialog, openFullDialog } = useFullDialog({ renderHtml, summary });

  useEffect(() => {
    if (defaultGptText) {
      setShowChildren(false);
    }
  }, [defaultGptText]);

  function getOperations() {
    if (showChildren) return [];
    if (isEmpty || status === 'loading') return [];
    if (isEmpty) return [refreshIcon];

    return [refreshIcon, copyIcon, fullIcon];
  }

   const { oriFilter } = useSelector(
      (state: any) => state.opinionAIShortIdFilter,
    );

  return (
    <>
      <CardCommon
        className="dbo-aigc-card-wrapper dbo-aigc-card-wrapper--multiple"
        title={showChildren ? '' : title}
        loading={loading}
        loadingHeight={300}
        operationInfo={['', () => {}]}
        operations={getOperations()}
        handleOperationsOnClick={({ symbol }) => {
          if (symbol === 'copy') {
            DBO_T.copyText(summary, formatMessage);
          } else if (symbol === 'refresh') {
            if (isEmpty) setShowChildren(true);
            else toggleVisible();
          } else if (symbol === 'fullscreen') {
            openFullDialog();
          }
        }}
        {...cardProps}
      >
        <div
          className="dbo-gpt-summary-flex"
          style={{ display: showChildren ? 'flex' : 'none' }}
        >
          {children(toggleVisible, { loading, total })}
        </div>
        <div
          className="dbo-gpt-summary-main"
          style={{ display: !showChildren ? 'block' : 'none' }}
        >
          <MultipleGptCardView
            hasStopBtn={hasStopBtn}
            handleStop={() => setShowChildren(true)}
            showDefaultGpt={showDefaultGpt}
            getTitle={getTitle}
            renderHtml={renderHtml}
            changeAllGptMap={changeAllGptMap}
            filter={filter}
            GPTList={GPTList}
            promptInfo={promptInfo}
            isEmpty={isEmpty}
          ></MultipleGptCardView>
        </div>
      </CardCommon>

      <PromptModal
        visible={visible}
        close={() => toggleVisible()}
        handleConfirm={(data, defaultAction) => {
          if (data.resultType === 'page') {
            setPromptInfo(data);
            setShowChildren(false);
            clearDefaultGpt();
          } else if (data.resultType === 'download') {
            downloadAction({
              key: 'GPTMultiple',
              fileName,
              filter,
              auxiliaryParams: {
                promptInfo: data,
                oriFilter: oriFilter,
                GPTList: GPTList.map(({ dealwithParams, ...otherInfo }) => ({
                  filter: dealwithParams?.(filter) || filter,
                  ...otherInfo,
                })),
              },
            });
          } else {
            defaultAction?.();
          }
          toggleVisible();
        }}
        filter={filter}
        promptList={promptList}
        hasAdvancedSummary={false} 
        needBasicTaskId={false}
        saveName={saveName}
      ></PromptModal>
      <FullDialog></FullDialog>
    </>
  );
}
